import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const PageTransition = ({ isActive, onComplete, targetUrl }) => {
  const [animationPhase, setAnimationPhase] = useState("idle"); // idle, entering, exiting
  const letters = ["U", "M", "M", "E", "S"];
  const location = useLocation();
  const isRunningRef = useRef(false);
  const timersRef = useRef({});

  useEffect(() => {
    // Clear any existing timers
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};

    if (isActive && !isRunningRef.current) {
      isRunningRef.current = true;
      setAnimationPhase("entering");

      // Check if this is a navigation to a different page or initial load
      const isNavigation = targetUrl && targetUrl !== location.pathname;

      // Wait for entrance, then navigate and exit
      timersRef.current.navigation = setTimeout(() => {
        if (onComplete && isNavigation) {
          onComplete(targetUrl);
        }
        setAnimationPhase("exiting");
      }, 2000); // Longer time to see the letters

      // Complete transition after exit animation
      timersRef.current.complete = setTimeout(() => {
        setAnimationPhase("idle");
        isRunningRef.current = false;
        if (onComplete && !isNavigation) {
          onComplete();
        }
      }, 3500); // Total transition time
    } else if (!isActive) {
      setAnimationPhase("idle");
      isRunningRef.current = false;
    }

    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
    };
  }, [isActive, onComplete, targetUrl, location.pathname]);

  if (!isActive && animationPhase === "idle") {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden">
      {letters.map((letter, index) => (
        <div
          key={index}
          className={`absolute w-1/5 h-full bg-gradient-to-b from-gray-900 via-gray-800 to-black flex items-center justify-center text-white text-4xl md:text-5xl lg:text-6xl font-bold tracking-widest shadow-2xl transition-all duration-1000 ease-out`}
          style={{
            left: `${index * 20}%`,
            transform: animationPhase === "entering" 
              ? "translateY(100%)" 
              : animationPhase === "exiting"
              ? "translateY(-100%)"
              : "translateY(0%)",
            transitionDelay: animationPhase === "entering" 
              ? `${index * 150}ms` 
              : animationPhase === "exiting" 
              ? `${index * 150}ms` 
              : "0ms",
            transitionTimingFunction: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          }}
        >
          <span className="drop-shadow-2xl">
            {letter}
          </span>
        </div>
      ))}
    </div>
  );
};

export default PageTransition;
